import { useEffect, useMemo, useRef, useState } from 'react';
import { getFormasiBanLayout, getFormasiBanViewHeight } from './formasiBanLayout';

export function FormasiBanDraggable({
  formasi,
  cadangan,
  onClick,
  onSelectBan,
  daftarKodeBan,
  highlightedPosisi = [],
  warningPosisi = [],
  unevenWearPosisi = [],
  highlightedStyle = 'default',
  onDragDrop,
  onDragStateChange,
  onBanHover,
  onBanLeave,
  resolveDragLabel,
  externalTargetPos = '',
  externalTargetActive = false,
  draggable = true,
  dragOwner = 'mt1',
}) {
  const dragMoveThreshold = 4;
  const dragStartRef = useRef({ x: 0, y: 0 });
  const hasMovedRef = useRef(false);
  const [dragSourceId, setDragSourceId] = useState('');
  const [dragSourceBanId, setDragSourceBanId] = useState(0);
  const [dragTargetId, setDragTargetId] = useState('');
  const [dragHasPair, setDragHasPair] = useState(false);
  const [dragGhost, setDragGhost] = useState({
    active: false,
    x: 0,
    y: 0,
    label: '',
  });
  const highlightedSet = useMemo(
    () => new Set((Array.isArray(highlightedPosisi) ? highlightedPosisi : [highlightedPosisi]).filter(Boolean)),
    [highlightedPosisi]
  );
  const warningSet = useMemo(
    () => new Set((Array.isArray(warningPosisi) ? warningPosisi : [warningPosisi]).filter(Boolean)),
    [warningPosisi]
  );
  const unevenWearSet = useMemo(
    () => new Set((Array.isArray(unevenWearPosisi) ? unevenWearPosisi : [unevenWearPosisi]).filter(Boolean)),
    [unevenWearPosisi]
  );

  const getBanPalette = (id, isMapped, isHighlighted, isWarning) => {
    const isCadangan = String(id || '').startsWith('S-');
    const isDragging = String(dragSourceId || '').trim() === String(id || '').trim();
    const isDropTarget = String(dragTargetId || '').trim() === String(id || '').trim();
    const isExternalDropTarget = Boolean(
      externalTargetActive
      && String(externalTargetPos || '').trim()
      && String(externalTargetPos || '').trim() === String(id || '').trim()
    );
    if (isDragging && dragHasPair) {
      return { fill: '#22c55e', stroke: '#166534', text: '#f0fdf4' };
    }
    if (isDragging) {
      return { fill: '#f59e0b', stroke: '#92400e', text: '#111827' };
    }
    if (isExternalDropTarget) {
      return { fill: '#22c55e', stroke: '#166534', text: '#f0fdf4' };
    }
    if (isDropTarget) {
      return { fill: '#22c55e', stroke: '#166534', text: '#f0fdf4' };
    }
    let base = null;
    if (isHighlighted) {
      if (String(highlightedStyle || '').trim() === 'success') {
        base = { fill: '#22c55e', stroke: '#166534', text: '#f0fdf4' };
      } else {
        base = { fill: '#0f1f4f', stroke: '#071033', text: '#eff6ff' };
      }
    } else if (isCadangan) {
      base = isMapped
        ? { fill: '#c17f28', stroke: '#7c4a05', text: '#fff7ed' }
        : { fill: '#f0e2c6', stroke: '#b0894f', text: '#6b4f1d' };
    } else if (isMapped) {
      base = { fill: '#2f6ea3', stroke: '#133f66', text: '#f8fafc' };
    } else {
      base = { fill: '#d7e3f1', stroke: '#8aa0b8', text: '#334155' };
    }
    if (isWarning) {
      return {
        ...base,
        stroke: '#dc2626',
        strokeWidth: 4,
        shadow: 'drop-shadow(0 0 6px rgba(244, 114, 182, 0.95))',
      };
    }
    return base;
  };

  const emitBanHover = (id, event) => {
    if (typeof onBanHover !== 'function') return;
    const pos = String(id || '').trim();
    if (!pos) return;
    const idBan = Number(daftarKodeBan?.[pos] || 0);
    if (!idBan) return;
    onBanHover({
      owner: String(dragOwner || '').trim() || 'mt1',
      pos,
      id_ban: idBan,
      x: Number(event?.clientX || 0),
      y: Number(event?.clientY || 0),
    });
  };
  const emitBanLeave = () => {
    if (typeof onBanLeave === 'function') onBanLeave();
  };
  const handleBanClick = (id) => {
    if (hasMovedRef.current) return;
    if (typeof onClick === 'function') onClick(id);
  };
  const parseDropSlot = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return null;
    const [owner, pos] = raw.split(':');
    const o = String(owner || '').trim();
    const p = String(pos || '').trim();
    if (!o || !p) return null;
    return { owner: o, pos: p };
  };
  const parseDropZone = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return null;
    const [owner, zone = 'formasi'] = raw.split(':');
    const o = String(owner || '').trim();
    const z = String(zone || '').trim() || 'formasi';
    if (!o) return null;
    return { owner: o, zone: z };
  };
  const clearDragState = () => {
    if (typeof onDragStateChange === 'function') {
      onDragStateChange({ active: false, source: null, target: null, hasPair: false });
    }
    setDragSourceId('');
    setDragSourceBanId(0);
    setDragTargetId('');
    setDragHasPair(false);
    setDragGhost({ active: false, x: 0, y: 0, label: '' });
    hasMovedRef.current = false;
  };
  const emitDrop = (targetMeta) => {
    const sourcePos = String(dragSourceId || '').trim();
    if (!sourcePos) return;
    if (typeof onDragDrop === 'function') {
      onDragDrop({
        source: {
          owner: String(dragOwner || '').trim() || 'mt1',
          pos: sourcePos,
          id_ban: Number(dragSourceBanId || 0),
          label: String(dragGhost?.label || sourcePos).trim(),
        },
        target: targetMeta || null,
      });
    }
  };
  const handleMouseDown = (id, event) => {
    if (!draggable) return;
    if (event?.button !== 0) return;
    const pos = String(id || '').trim();
    if (!pos) return;
    const idBan = Number(daftarKodeBan?.[pos] || 0);
    if (!idBan) return;
    const dragLabel = String(
      (typeof resolveDragLabel === 'function' ? resolveDragLabel({ pos, id_ban: idBan, owner: dragOwner }) : '')
      || pos
    ).trim();
    dragStartRef.current = { x: Number(event?.clientX || 0), y: Number(event?.clientY || 0) };
    hasMovedRef.current = false;
    setDragSourceId(pos);
    setDragSourceBanId(idBan);
    setDragTargetId('');
    setDragGhost({
      active: true,
      x: Number(event?.clientX || 0),
      y: Number(event?.clientY || 0),
      label: dragLabel,
    });
  };
  useEffect(() => {
    if (!dragGhost.active || !dragSourceId) return undefined;
    const onMouseMove = (event) => {
      const x = Number(event?.clientX || 0);
      const y = Number(event?.clientY || 0);
      const dx = Math.abs(x - Number(dragStartRef.current?.x || 0));
      const dy = Math.abs(y - Number(dragStartRef.current?.y || 0));
      if (!hasMovedRef.current && (dx > dragMoveThreshold || dy > dragMoveThreshold)) {
        hasMovedRef.current = true;
      }
      setDragGhost((prev) => ({ ...prev, x, y, active: true }));
      const hit = document.elementFromPoint(x, y);
      const slotRaw = hit?.closest?.('[data-drop-slot]')?.getAttribute?.('data-drop-slot');
      const zoneRaw = hit?.closest?.('[data-drop-zone]')?.getAttribute?.('data-drop-zone');
      const slot = parseDropSlot(slotRaw);
      const zone = parseDropZone(zoneRaw);
      const validSlot = Boolean(slot && !(slot.owner === dragOwner && slot.pos === dragSourceId));
      const validZone = Boolean(zone && zone.owner !== dragOwner);
      const hasPair = validSlot || validZone;
      setDragHasPair(hasPair);
      if (typeof onDragStateChange === 'function') {
        onDragStateChange({
          active: true,
          hasPair,
          source: {
            owner: String(dragOwner || '').trim() || 'mt1',
            pos: String(dragSourceId || '').trim(),
            id_ban: Number(dragSourceBanId || 0),
            label: String(dragGhost?.label || dragSourceId).trim(),
          },
          target: validSlot
            ? { kind: 'slot', owner: slot.owner, pos: slot.pos }
            : (validZone ? { kind: 'zone', owner: zone.owner, zone: zone.zone } : null),
        });
      }
      if (!validSlot) {
        setDragTargetId('');
        return;
      }
      setDragTargetId(slot.owner === dragOwner ? slot.pos : '');
    };
    const onMouseUp = (event) => {
      if (!hasMovedRef.current) {
        const selectedPos = String(dragSourceId || '').trim();
        if (selectedPos && typeof onSelectBan === 'function') onSelectBan(selectedPos);
        clearDragState();
        return;
      }
      const x = Number(event?.clientX || 0);
      const y = Number(event?.clientY || 0);
      const hit = document.elementFromPoint(x, y);
      const slotRaw = hit?.closest?.('[data-drop-slot]')?.getAttribute?.('data-drop-slot');
      const zoneRaw = hit?.closest?.('[data-drop-zone]')?.getAttribute?.('data-drop-zone');
      const slot = parseDropSlot(slotRaw);
      const zone = parseDropZone(zoneRaw);
      let targetMeta = null;
      if (slot) {
        targetMeta = { kind: 'slot', owner: slot.owner, pos: slot.pos };
      } else if (zone) {
        targetMeta = { kind: 'zone', owner: zone.owner, zone: zone.zone };
      }
      emitDrop(targetMeta);
      clearDragState();
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp, { once: true });
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [dragGhost.active, dragSourceId, dragOwner, dragSourceBanId, onDragStateChange, onSelectBan, dragGhost?.label, resolveDragLabel]);

  const Ban = ({ x, y, label, id, w = 60, h = 100, rx = 16, ry = 16 }) => {
    const isMapped = Boolean(daftarKodeBan?.[id]);
    const isHighlighted = highlightedSet.has(id);
    const isWarning = warningSet.has(id);
    const isUnevenWear = unevenWearSet.has(id);
    const palette = getBanPalette(id, isMapped, isHighlighted, isWarning);
    return (
      <g
        onClick={() => handleBanClick(id)}
        style={{ cursor: "pointer" }}
        onMouseDown={(e) => handleMouseDown(id, e)}
        onMouseEnter={(e) => emitBanHover(id, e)}
        onMouseMove={(e) => emitBanHover(id, e)}
        onMouseLeave={emitBanLeave}
        data-ban-hover="1"
        data-drop-slot={`${dragOwner}:${id}`}
      >
        <rect
          x={x}
          y={y}
          width={w}
          height={h}
          rx={rx}
          ry={ry}
          fill={palette.fill}
          stroke={palette.stroke}
          strokeWidth={Number(palette?.strokeWidth || 2)}
          style={{ filter: palette?.shadow || 'none' }}
          data-drop-slot={`${dragOwner}:${id}`}
        />
        <text
          x={x + w / 2}
          y={y + h / 2 + 5}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={w * 0.3}
          fontWeight="bold"
          fill={isUnevenWear ? '#7f1d1d' : palette.text}
          style={{ filter: isUnevenWear ? 'drop-shadow(0 0 1px #ffffff) drop-shadow(0 0 2px #ffffff)' : 'none' }}
          data-drop-slot={`${dragOwner}:${id}`}
        >
          {label}
        </text>
      </g>
    );
  };

  const { lines, bans } = useMemo(
    () => getFormasiBanLayout(formasi, cadangan),
    [formasi, cadangan]
  );

  return (
    <>
    <div className="formasi-ban-surface rounded-4 position-relative" data-drop-zone={`${dragOwner}:formasi`}>
    <svg
    viewBox={`0 0 420 ${getFormasiBanViewHeight(formasi)}`}
    width="100%"
    height="100%"
    preserveAspectRatio="xMidYMid meet"
    style={{ display: "block" }}
    >
      {lines.map((line, idx) => (
        <line key={`ln-${idx}`} stroke="#8ea6c1" strokeWidth={8} {...line} />
      ))}
      {bans.map((ban) => (
        <Ban key={ban.id} {...ban} />
      ))}
    </svg>
    {dragGhost.active && (
      <div
        style={{
          position: 'fixed',
          left: `${dragGhost.x + 14}px`,
          top: `${dragGhost.y + 14}px`,
          zIndex: 9999,
          pointerEvents: 'none',
          minWidth: '76px',
          padding: '6px 10px',
          borderRadius: '12px',
          background: 'linear-gradient(145deg, #f59e0b 0%, #f97316 100%)',
          border: '2px solid #9a3412',
          color: '#111827',
          fontWeight: 700,
          fontSize: '0.85rem',
          boxShadow: '0 10px 24px rgba(0,0,0,0.28)',
          transform: 'translate(-20%, -45%)',
        }}
      >
        {dragGhost.label || dragSourceId}
      </div>
    )}
    </div>
    </>
  );
}
